import _ from 'lodash'
import moment from 'moment'
import Decimal from 'decimal.js'

/**
 * 销售业绩计算工具类 - 支持周目标和半年目标
 */
export class PerformanceCalculationUtils {

  /**
   * 安全的数值转换
   * @param {any} value - 待转换的值
   * @returns {number} 转换后的数字，无效值返回0
   */
  static safeNumber(value) {
    const num = parseFloat(value)
    return isNaN(num) ? 0 : num
  }

  /**
   * 使用 Decimal.js 进行精确求和
   * @param {array} values - 数值数组
   * @returns {number} 求和结果
   */
  static decimalSum(values) {
    if (!values || values.length === 0) return 0

    let sum = new Decimal(0)
    values.forEach(value => {
      if (value !== null && value !== undefined && value !== '' && !isNaN(value)) {
        sum = sum.plus(new Decimal(String(value)))
      }
    })

    return parseFloat(sum.toString())
  }

  /**
   * 使用 Decimal.js 进行精确除法
   * @param {number} numerator - 分子
   * @param {number} denominator - 分母
   * @returns {number} 除法结果，分母为0时返回0
   */
  static decimalDivide(numerator, denominator) {
    if (denominator === 0 || denominator === null || denominator === undefined) return 0
    if (numerator === null || numerator === undefined) return 0

    const num = new Decimal(String(numerator))
    const den = new Decimal(String(denominator))

    return parseFloat(num.dividedBy(den).toString())
  }

  /**
   * 使用 Decimal.js 进行精确减法
   * @param {number} a - 被减数
   * @param {number} b - 减数
   * @returns {number} 减法结果
   */
  static decimalSubtract(a, b) {
    const numA = new Decimal(String(a || 0))
    const numB = new Decimal(String(b || 0))

    return parseFloat(numA.minus(numB).toString())
  }

  /**
   * 格式化百分比显示
   * @param {number} value - 数值
   * @param {number} decimals - 小数位数，默认2位
   * @returns {string} 百分比字符串
   */
  static formatPercentage(value, decimals = 2) {
    const numValue = this.safeNumber(value)
    const percentage = (numValue * 100).toFixed(decimals)

    // 如果是 0.00%，直接显示 0%
    if (parseFloat(percentage) === 0) {
      return '0%'
    }

    // 移除末尾不必要的0和小数点
    return parseFloat(percentage) + '%'
  }

  /**
   * 格式化数值显示（普通数值，不转换单位）
   * @param {number} value - 数值
   * @param {number} decimals - 小数位数，默认2位
   * @returns {string} 格式化后的数值字符串
   */
  static formatNumber(value, decimals = 2) {
    const numValue = this.safeNumber(value)

    // 如果是 0.00，直接显示 0
    if (numValue === 0) {
      return '0'
    }

    // 移除末尾不必要的0和小数点
    return parseFloat(numValue.toFixed(decimals)).toString()
  }

  /**
   * 格式化金额显示（转换为万元单位）
   * @param {number} value - 数值
   * @returns {string} 格式化后的金额字符串
   */
  static formatCurrency(value) {
    // 先转换为字符串以保留原始精度
    let valueStr = String(value)

    // 处理空值和无效值
    if (!valueStr || valueStr === 'null' || valueStr === 'undefined' || valueStr === 'NaN') {
      return '0'
    }

    // 使用 Decimal.js 进行精确计算（传入字符串避免浮点数精度问题）
    const decimalValue = new Decimal(valueStr)

    // 如果是 0，直接显示 0
    if (decimalValue.isZero()) {
      return '0'
    }

    const wanValue = decimalValue.dividedBy(10000)

    // 转换为字符串，保留所有有效数字
    let wanValueStr = wanValue.toString()

    // 计算原始数据的小数位数
    const originalParts = valueStr.split('.')
    const originalDecimalPlaces = originalParts[1] ? originalParts[1].length : 0

    // 如果有小数部分，需要根据原始数据的小数位数来限制结果
    if (originalDecimalPlaces > 0) {
      // 原始数据的总有效位数
      const wanParts = wanValueStr.split('.')
      if (wanParts[1]) {
        // 限制小数位数：原始小数位数 + 整数部分减少的位数（最多4位）
        const integerReduction = Math.max(0, originalParts[0].length - wanParts[0].length)
        const maxDecimalPlaces = originalDecimalPlaces + integerReduction

        // 如果当前小数位数超过限制，进行截断（不四舍五入）
        if (wanParts[1].length > maxDecimalPlaces) {
          wanValueStr = wanParts[0] + '.' + wanParts[1].substring(0, maxDecimalPlaces)
        }
      }
    }

    // 手动添加千分位分隔符，同时保留所有小数位
    const parts = wanValueStr.split('.')
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const decimalPart = parts[1] ? '.' + parts[1] : ''

    return integerPart + decimalPart + 'w'
  }

  // ====================
  // 团队和小组业绩计算方法
  // ====================

  /**
   * 计算团队目标（团队所有成员的目标总和）
   * @param {object} team - 团队数据
   * @returns {number} 团队目标
   */
  static getTeamOrderTarget(team) {
    if (!team) return 0

    const values = []
    const processedIds = new Set() // 记录已处理的用户ID，避免重复计算

    // 如果队长有业务数据，包含队长目标
    if (team.leader_data && team.leader_data.weekly && team.leader_data.id) {
      values.push(this.getPersonMonthOrderTarget(team.leader_data))
      processedIds.add(team.leader_data.id)
    }

    // 计算所有小组的目标，跳过已处理的ID
    if (team.children && team.children.length > 0) {
      team.children.forEach(group => {
        values.push(this.getGroupOrderTarget(group, processedIds))
      })
    }

    return this.decimalSum(values)
  }

  /**
   * 计算团队下单（团队所有成员的下单总和）
   * @param {object} team - 团队数据
   * @returns {number} 团队下单
   */
  static getTeamOrderActual(team) {
    if (!team) return 0

    const values = []
    const processedIds = new Set() // 记录已处理的用户ID，避免重复计算

    // 如果队长有业务数据，包含队长下单
    if (team.leader_data && team.leader_data.weekly && team.leader_data.id) {
      values.push(this.getPersonMonthOrderActual(team.leader_data))
      processedIds.add(team.leader_data.id)
    }

    // 计算所有小组的下单，跳过已处理的ID
    if (team.children && team.children.length > 0) {
      team.children.forEach(group => {
        values.push(this.getGroupOrderActual(group, processedIds))
      })
    }

    return this.decimalSum(values)
  }

  /**
   * 计算小组目标（小组所有成员的目标总和）
   * @param {object} group - 小组数据
   * @param {Set} processedIds - 已处理的用户ID集合（可选），用于避免重复计算
   * @returns {number} 小组目标
   */
  static getGroupOrderTarget(group, processedIds = null) {
    if (!group) return 0

    const values = []

    // 包含组长目标（检查是否已处理过）
    if (group.leader_data && group.leader_data.weekly && group.leader_data.id) {
      if (!processedIds || !processedIds.has(group.leader_data.id)) {
        values.push(this.getPersonMonthOrderTarget(group.leader_data))
        if (processedIds) {
          processedIds.add(group.leader_data.id)
        }
      }
    }

    // 包含组员目标（排除组长，并检查是否已处理过）
    if (group.data && Array.isArray(group.data)) {
      group.data.forEach(member => {
        if (member.weekly && member.id &&
            (!group.leader_data || member.id !== group.leader_data.id) &&
            (!processedIds || !processedIds.has(member.id))) {
          values.push(this.getPersonMonthOrderTarget(member))
          if (processedIds) {
            processedIds.add(member.id)
          }
        }
      })
    }

    return this.decimalSum(values)
  }

  /**
   * 计算小组下单（小组所有成员的下单总和）
   * @param {object} group - 小组数据
   * @param {Set} processedIds - 已处理的用户ID集合（可选），用于避免重复计算
   * @returns {number} 小组下单
   */
  static getGroupOrderActual(group, processedIds = null) {
    if (!group) return 0

    const values = []

    // 包含组长下单（检查是否已处理过）
    if (group.leader_data && group.leader_data.weekly && group.leader_data.id) {
      if (!processedIds || !processedIds.has(group.leader_data.id)) {
        values.push(this.getPersonMonthOrderActual(group.leader_data))
        if (processedIds) {
          processedIds.add(group.leader_data.id)
        }
      }
    }

    // 包含组员下单（排除组长，并检查是否已处理过）
    if (group.data && Array.isArray(group.data)) {
      group.data.forEach(member => {
        if (member.weekly && member.id &&
            (!group.leader_data || member.id !== group.leader_data.id) &&
            (!processedIds || !processedIds.has(member.id))) {
          values.push(this.getPersonMonthOrderActual(member))
          if (processedIds) {
            processedIds.add(member.id)
          }
        }
      })
    }

    return this.decimalSum(values)
  }

  /**
   * 计算小组达成率
   * @param {object} group - 小组数据
   * @returns {number} 小组达成率
   */
  static getGroupAchievementRate(group) {
    const target = this.getGroupOrderTarget(group)
    const actual = this.getGroupOrderActual(group)

    return this.decimalDivide(actual, target)
  }

  // ====================
  // 周目标相关方法
  // ====================

  /**
   * 计算小组的月度目标
   * @param {object} group - 小组数据
   * @returns {number} 月度目标
   */
  static getMonthOrderTarget(group) {
    if (!group || !group.datum || !group.datum.weekly) return 0
    return _.sumBy(group.datum.weekly, item => this.safeNumber(item.order_target))
  }

  /**
   * 计算小组的月度达成
   * @param {object} group - 小组数据
   * @returns {number} 月度达成
   */
  static getMonthOrderActual(group) {
    if (!group || !group.datum || !group.datum.weekly) return 0
    return _.sumBy(group.datum.weekly, item => this.safeNumber(item.order_actual))
  }

  /**
   * 计算小组的月度达成率
   * @param {object} group - 小组数据
   * @returns {number} 达成率（0-1之间的小数）
   */
  static getMonthAchievementRate(group) {
    const target = this.getMonthOrderTarget(group)
    const actual = this.getMonthOrderActual(group)
    return this.decimalDivide(actual, target)
  }

  /**
   * 计算个人的月度目标
   * @param {object} personData - 个人数据（包含weekly数组）
   * @returns {number} 月度目标
   */
  static getPersonMonthOrderTarget(personData) {
    if (!personData || !personData.weekly) return 0
    const values = personData.weekly.map(item => this.safeNumber(item.order_target))
    return this.decimalSum(values)
  }

  /**
   * 计算个人的月度达成
   * @param {object} personData - 个人数据（包含weekly数组）
   * @returns {number} 月度达成
   */
  static getPersonMonthOrderActual(personData) {
    if (!personData || !personData.weekly) return 0
    const values = personData.weekly.map(item => this.safeNumber(item.order_actual))
    return this.decimalSum(values)
  }

  /**
   * 计算个人的月度达成率
   * @param {object} personData - 个人数据（包含weekly数组）
   * @returns {number} 达成率（0-1之间的小数）
   */
  static getPersonMonthAchievementRate(personData) {
    const target = this.getPersonMonthOrderTarget(personData)
    const actual = this.getPersonMonthOrderActual(personData)
    return this.decimalDivide(actual, target)
  }

  /**
   * 计算个人/组长/队长的新客户达成（周目标）
   * @param {object} personData - 个人数据（包含weekly数组）
   * @returns {number} 新客户达成
   */
  static getWeeklyNewCustomerAchievement(personData) {
    if (!personData || !personData.weekly) return 0

    const values = personData.weekly.map(week => {
      return this.safeNumber(week.new_customer_order_actual)
    })
    return this.decimalSum(values)
  }

  static getWeeklyGoldenOldCustomerAchievement(personData) {
    if (!personData || !personData.weekly) return 0

    const values = personData.weekly.map(week => {
      return this.safeNumber(week.golden_old_customer_order_actual)
    })
    return this.decimalSum(values)
  }

  /**
   * 计算个人的新客户占比（周目标）
   * @param {object} personData - 个人数据（包含weekly数组）
   * @returns {number} 新客户占比（0-1之间的小数）
   */
  static getWeeklyNewCustomerRatio(personData) {
    const newCustomerAchievement = this.getWeeklyNewCustomerAchievement(personData)
    const monthOrderActual = this.getPersonMonthOrderActual(personData)
    return this.decimalDivide(newCustomerAchievement, monthOrderActual)
  }

  /**
   * 计算个人的老客户占比（周目标）
   * @param {object} personData - 个人数据（包含weekly数组）
   * @returns {number} 老客户占比（0-1之间的小数）
   */
  static getWeeklyOldCustomerRatio(personData, kind) {
    const monthOrderActual = this.getPersonMonthOrderActual(personData)
    const goldenOldCustomerAchievement = this.getWeeklyGoldenOldCustomerAchievement(personData)
    if (kind === 'golden') {
      return this.decimalDivide(goldenOldCustomerAchievement, monthOrderActual)
    } else {
      const newCustomerAchievement = this.getWeeklyNewCustomerAchievement(personData)
      const oldCustomerValue = this.decimalSubtract(monthOrderActual, newCustomerAchievement)
      return this.decimalDivide(oldCustomerValue, monthOrderActual)
    }
  }

  /**
   * 计算团队的达成率（周目标）
   * @param {object} team - 团队数据
   * @returns {number} 团队达成率（0-1之间的小数）
   */
  static getWeeklyTeamAchievementRate(team) {
    if (!team) return 0

    const targetValues = []
    const actualValues = []
    const processedIds = new Set() // 记录已处理的用户ID，避免重复计算

    // 1. 计算队长的业绩数据
    if (team.leader_data && team.leader_data.weekly && team.leader_data.id) {
      targetValues.push(this.getPersonMonthOrderTarget(team.leader_data))
      actualValues.push(this.getPersonMonthOrderActual(team.leader_data))
      processedIds.add(team.leader_data.id)
    }

    // 2. 计算所有小组的业绩数据（包括组长和组员，跳过已处理的ID）
    if (team.children && team.children.length > 0) {
      team.children.forEach(group => {
        // 2.1 组长业绩数据（检查是否已处理过）
        if (group.leader_data && group.leader_data.weekly && group.leader_data.id) {
          if (!processedIds.has(group.leader_data.id)) {
            targetValues.push(this.getPersonMonthOrderTarget(group.leader_data))
            actualValues.push(this.getPersonMonthOrderActual(group.leader_data))
            processedIds.add(group.leader_data.id)
          }
        }

        // 2.2 组员业绩数据（排除组长，并检查是否已处理过）
        if (group.data && Array.isArray(group.data)) {
          group.data.forEach(member => {
            // 排除组长，并检查是否已处理过
            if (member.weekly && member.id &&
                (!group.leader_data || member.id !== group.leader_data.id) &&
                !processedIds.has(member.id)) {
              targetValues.push(this.getPersonMonthOrderTarget(member))
              actualValues.push(this.getPersonMonthOrderActual(member))
              processedIds.add(member.id)
            }
          })
        }
      })
    }

    const teamTotalTarget = this.decimalSum(targetValues)
    const teamTotalActual = this.decimalSum(actualValues)
    return this.decimalDivide(teamTotalActual, teamTotalTarget)
  }

  /**
   * 计算周度达成率
   * @param {object} weekData - 周数据
   * @returns {number} 周达成率（0-1之间的小数）
   */
  static getWeekAchievementRate(weekData) {
    const target = this.safeNumber(weekData.order_target)
    const actual = this.safeNumber(weekData.order_actual)
    return this.decimalDivide(actual, target)
  }

  /**
   * 获取周度标题
   * @param {object} weekData - 周数据
   * @param {number} index - 周索引
   * @returns {string} 周度标题
   */
  static getWeekTitle(weekData, index) {
    if (weekData.start_date && weekData.end_date) {
      const startDate = moment(weekData.start_date).format('MM/DD')
      const endDate = moment(weekData.end_date).format('MM/DD')
      return `第${index + 1}周(${startDate}-${endDate})`
    }
    return `第${index + 1}周`
  }

  /**
   * 计算个人月度排名（周目标）
   * @param {array} teamsData - 原始团队数据
   * @returns {Map} 个人ID到排名的映射
   */
  static calculateMonthRanking(teamsData) {
    const allPersons = []
    const rankingMap = new Map()

    // 收集所有个人的月度达成数据
    teamsData.forEach(team => {
      // 收集队长数据
      if (team.leader_data && team.leader_data.weekly) {
        const monthOrderActual = this.getPersonMonthOrderActual(team.leader_data)
        allPersons.push({
          personId: `team_leader_${team.id}`,
          monthOrderActual: monthOrderActual
        })
      }

      // 收集小组数据
      if (team.children) {
        team.children.forEach(group => {
          // 收集组长数据
          if (group.leader && group.leader_data && group.leader_data.weekly) {
            const monthOrderActual = this.getPersonMonthOrderActual(group.leader_data)
            allPersons.push({
              personId: `group_leader_${group.id}`,
              monthOrderActual: monthOrderActual
            })
          }

          // 收集队员数据（排除组长）
          if (group.data && Array.isArray(group.data)) {
            group.data.forEach((member, memberIndex) => {
              if (member.weekly && (!group.leader_data || member.id !== group.leader_data.id)) {
                const monthOrderActual = this.getPersonMonthOrderActual(member)
                allPersons.push({
                  personId: `member_${group.id}_${memberIndex}`,
                  monthOrderActual: monthOrderActual
                })
              }
            })
          }
        })
      }
    })

    // 分离有效数据和零值数据
    const validPersons = allPersons.filter(person => person.monthOrderActual > 0)
    const zeroPersons = allPersons.filter(person => person.monthOrderActual === 0)

    // 对有效数据按月度达成降序排序
    validPersons.sort((a, b) => b.monthOrderActual - a.monthOrderActual)

    // 计算有效数据的排名（处理相同数据共用排名）
    let currentRank = 1
    let previousValue = null

    validPersons.forEach((person, index) => {
      if (previousValue !== null && person.monthOrderActual !== previousValue) {
        // 数值不同，更新排名（跳跃式排名）
        currentRank = index + 1
      }

      rankingMap.set(person.personId, currentRank)
      previousValue = person.monthOrderActual
    })

    // 零值数据统一显示"-"
    zeroPersons.forEach(person => {
      rankingMap.set(person.personId, '-')
    })

    return rankingMap
  }

  /**
   * 计算各周排名
   * @param {array} teamsData - 原始团队数据
   * @returns {Map} 个人ID+周索引到排名的映射
   */
  static calculateWeeklyRanking(teamsData) {
    const weeklyRankingMap = new Map()

    // 获取最大周数
    let weekCount = 0
    teamsData.forEach(team => {
      if (team.leader_data && team.leader_data.weekly) {
        weekCount = Math.max(weekCount, team.leader_data.weekly.length)
      }
      if (team.children) {
        team.children.forEach(group => {
          if (group.data && Array.isArray(group.data)) {
            group.data.forEach(member => {
              if (member.weekly) {
                weekCount = Math.max(weekCount, member.weekly.length)
              }
            })
          }
        })
      }
    })

    // 为每一周计算排名
    for (let weekIndex = 0; weekIndex < weekCount; weekIndex++) {
      const weekPersons = []

      // 收集所有个人的第weekIndex周数据
      teamsData.forEach(team => {
        // 收集队长数据
        if (team.leader_data && team.leader_data.weekly && team.leader_data.weekly[weekIndex]) {
          const weekData = team.leader_data.weekly[weekIndex]
          const weekActual = this.safeNumber(weekData.order_actual)
          weekPersons.push({
            personId: `team_leader_${team.id}`,
            weekIndex: weekIndex,
            weekActual: weekActual
          })
        }

        // 收集小组数据
        if (team.children) {
          team.children.forEach(group => {
            // 收集组长数据
            if (group.leader && group.leader_data && group.leader_data.weekly && group.leader_data.weekly[weekIndex]) {
              const weekData = group.leader_data.weekly[weekIndex]
              const weekActual = this.safeNumber(weekData.order_actual)
              weekPersons.push({
                personId: `group_leader_${group.id}`,
                weekIndex: weekIndex,
                weekActual: weekActual
              })
            }

            // 收集队员数据（排除组长）
            if (group.data && Array.isArray(group.data)) {
              group.data.forEach((member, memberIndex) => {
                if (member.weekly && member.weekly[weekIndex] && (!group.leader_data || member.id !== group.leader_data.id)) {
                  const weekData = member.weekly[weekIndex]
                  const weekActual = this.safeNumber(weekData.order_actual)
                  weekPersons.push({
                    personId: `member_${group.id}_${memberIndex}`,
                    weekIndex: weekIndex,
                    weekActual: weekActual
                  })
                }
              })
            }
          })
        }
      })

      // 分离有效数据和零值数据
      const validWeekPersons = weekPersons.filter(person => person.weekActual > 0)
      const zeroWeekPersons = weekPersons.filter(person => person.weekActual === 0)

      // 对有效数据按该周达成降序排序
      validWeekPersons.sort((a, b) => b.weekActual - a.weekActual)

      // 计算该周排名（处理相同数据共用排名）
      let currentRank = 1
      let previousValue = null

      validWeekPersons.forEach((person, index) => {
        if (previousValue !== null && person.weekActual !== previousValue) {
          // 数值不同，更新排名（跳跃式排名）
          currentRank = index + 1
        }

        const key = `${person.personId}_${person.weekIndex}`
        weeklyRankingMap.set(key, currentRank)
        previousValue = person.weekActual
      })

      // 零值数据统一显示"-"
      zeroWeekPersons.forEach(person => {
        const key = `${person.personId}_${person.weekIndex}`
        weeklyRankingMap.set(key, '-')
      })
    }

    return weeklyRankingMap
  }

  // ====================
  // 半年目标相关方法
  // ====================

  /**
   * 计算新客户达成总额（半年目标）
   * @param {object} newCustomerData - 新客户每日数据对象
   * @returns {number} 新客户达成总额
   */
  static calculateNewCustomerTotal(newCustomerData) {
    if (!!newCustomerData && typeof newCustomerData === 'object') {
      const values = Object.values(newCustomerData).map(dailyAmount => this.safeNumber(dailyAmount))
      return this.decimalSum(values)
    } else {
      return newCustomerData || 0
    }
  }

  static calculateGoldenOldCustomerTotal(goldenOldCustomerData) {
    if (!!goldenOldCustomerData && typeof goldenOldCustomerData === 'object') {
      const values = Object.values(goldenOldCustomerData).map(dailyAmount => this.safeNumber(dailyAmount))
      return this.decimalSum(values)
    } else {
      return goldenOldCustomerData || 0
    }
  }

  /**
   * 根据日期判断是上半年还是下半年
   * @param {string} dateStr - 日期字符串
   * @returns {string} "上半年" 或 "下半年"
   */
  static getHalfYearLabel(dateStr) {
    const date = moment(dateStr)
    const month = date.month() + 1 // moment月份从0开始

    return month <= 6 ? '上半年' : '下半年'
  }

  // ====================
  // 通用工具方法
  // ====================

  /**
   * 生成半年时间段选项
   * @param {number} startYear - 开始年份，默认为当前年份
   * @param {number} endYear - 结束年份，默认为2020
   * @returns {Array} 半年选项数组
   */
  static generateHalfYearOptions(startYear = null, endYear = 2020) {
    const options = []
    const currentYear = startYear || moment().year()

    // 从当前年份到结束年份
    for (let year = currentYear; year >= endYear; year--) {
      options.push({
        label: `${year}年下半年`,
        value: `${year}-07-01`
      })
      options.push({
        label: `${year}年上半年`,
        value: `${year}-01-01`
      })
    }

    return options
  }

  /**
   * 获取当前半年选项
   * @returns {string} 当前半年的值（YYYY-MM-DD格式）
   */
  static getCurrentHalfYearOption() {
    const currentYear = moment().year()
    const currentMonth = moment().month() + 1 // moment月份从0开始

    if (currentMonth <= 6) {
      return `${currentYear}-01-01` // 上半年
    } else {
      return `${currentYear}-07-01` // 下半年
    }
  }

  /**
   * 计算半年时间进度
   * @returns {number} 半年时间进度百分比
   */
  static getHalfYearProgress() {
    const currentDate = moment()
    const currentMonth = currentDate.month() + 1 // moment月份从0开始

    if (currentMonth <= 6) {
      // 上半年：1-6月
      const yearStart = currentDate.clone().startOf('year')
      const halfYearEnd = currentDate.clone().month(5).endOf('month') // 6月底
      const totalDays = halfYearEnd.diff(yearStart, 'days') + 1
      const passedDays = currentDate.diff(yearStart, 'days') + 1
      return Math.round((passedDays / totalDays) * 100)
    } else {
      // 下半年：7-12月
      const halfYearStart = currentDate.clone().month(6).startOf('month') // 7月初
      const yearEnd = currentDate.clone().endOf('year')
      const totalDays = yearEnd.diff(halfYearStart, 'days') + 1
      const passedDays = currentDate.diff(halfYearStart, 'days') + 1
      return Math.round((passedDays / totalDays) * 100)
    }
  }

  /**
   * 计算月度时间进度
   * @returns {number} 月度时间进度百分比
   */
  static getMonthProgress() {
    const currentDate = moment()
    const monthStart = currentDate.clone().startOf('month')
    const monthEnd = currentDate.clone().endOf('month')
    const totalDays = monthEnd.diff(monthStart, 'days') + 1
    const passedDays = currentDate.diff(monthStart, 'days') + 1

    return Math.round((passedDays / totalDays) * 100)
  }

  /**
   * 计算季度时间进度
   * @returns {number} 季度时间进度百分比
   */
  static getQuarterProgress() {
    const currentDate = moment()
    const quarterStart = currentDate.clone().startOf('quarter')
    const quarterEnd = currentDate.clone().endOf('quarter')
    const totalDays = quarterEnd.diff(quarterStart, 'days') + 1
    const passedDays = currentDate.diff(quarterStart, 'days') + 1

    return Math.round((passedDays / totalDays) * 100)
  }

  /**
   * 格式化月份显示
   * @param {string} monthStr - 月份字符串
   * @returns {string} 格式化后的月份
   */
  static formatMonth(monthStr) {
    if (!monthStr) return ''
    if (monthStr.includes('年')) {
      const match = monthStr.match(/(\d+)月/)
      return match ? `${match[1]}月` : monthStr
    }
    return monthStr
  }

  /**
   * 生成Excel列宽配置
   * @param {Array} columnWidths - 列宽数组
   * @returns {Array} Excel列宽配置
   */
  static generateExcelColumnWidths(columnWidths) {
    return columnWidths.map(width => ({ wch: width }))
  }

  /**
   * 从团队数据中提取团队选项
   * @param {Array} tableRows - 表格行数据
   * @returns {Array} 团队选项数组
   */
  static extractTeamOptions(tableRows) {
    if (!tableRows || tableRows.length === 0) {
      return []
    }

    const teams = new Set()
    tableRows.forEach(row => {
      if (row.teamName && row.teamName.trim()) {
        teams.add(row.teamName)
      }
    })

    return Array.from(teams).map(teamName => ({
      label: teamName,
      value: teamName
    }))
  }

  /**
   * 计算团队达成率（半年目标）
   * @param {array} teamChildren - 团队下的小组数据
   * @returns {number} 团队达成率
   */
  static getHalfYearTeamAchievementRate(teamChildren) {
    if (!Array.isArray(teamChildren) || teamChildren.length === 0) {
      return 0
    }

    const targetValues = []
    const actualValues = []

    teamChildren.forEach(group => {
      const halfYearlyData = group.datum && group.datum.half_yearly && group.datum.half_yearly[0]
      if (halfYearlyData) {
        targetValues.push(this.safeNumber(halfYearlyData.order_target))
        actualValues.push(this.safeNumber(halfYearlyData.order_actual))
      }
    })

    const totalTarget = this.decimalSum(targetValues)
    const totalActual = this.decimalSum(actualValues)
    return this.decimalDivide(totalActual, totalTarget)
  }

  /**
   * 计算半年度排名
   * @param {array} teamsData - 所有团队数据
   * @returns {Map} 小组ID到排名的映射
   */
  static calculateHalfYearlyRanking(teamsData) {
    const rankingMap = new Map()
    const allGroups = []

    // 收集所有小组数据
    teamsData.forEach(team => {
      if (team.children && Array.isArray(team.children)) {
        team.children.forEach(group => {
          const halfYearlyData = group.datum && group.datum.half_yearly && group.datum.half_yearly[0]
          if (halfYearlyData) {
            allGroups.push({
              groupId: group.id,
              orderActual: this.safeNumber(halfYearlyData.order_actual)
            })
          }
        })
      }
    })

    // 分离有效数据和零值数据
    const validGroups = allGroups.filter(group => group.orderActual > 0)
    const zeroGroups = allGroups.filter(group => group.orderActual === 0)

    // 按达成金额降序排序
    validGroups.sort((a, b) => b.orderActual - a.orderActual)

    // 计算排名（支持并列排名）
    let currentRank = 1
    let previousValue = null

    validGroups.forEach((group, index) => {
      if (previousValue !== null && group.orderActual !== previousValue) {
        // 数值不同，更新排名（跳跃式排名）
        currentRank = index + 1
      }

      rankingMap.set(group.groupId, currentRank)
      previousValue = group.orderActual
    })

    // 零值数据统一显示"-"
    zeroGroups.forEach(group => {
      rankingMap.set(group.groupId, '-')
    })

    return rankingMap
  }

  // ====================
  // 数据处理方法
  // ====================

  /**
   * 处理周目标表格数据
   * @param {array} teamsData - 原始团队数据
   * @param {string} queryDate - 查询日期
   * @returns {object} 处理后的数据
   */
  static processWeeklyTableData(teamsData, queryDate = '') {
    if (!teamsData || !Array.isArray(teamsData)) {
      return { rows: [], weekColumns: [] }
    }

    const rows = []
    let weekColumns = []

    // 计算排名（在处理表格数据前先计算好所有排名）
    const monthRankingMap = this.calculateMonthRanking(teamsData)
    const weeklyRankingMap = this.calculateWeeklyRanking(teamsData)

    teamsData.forEach(team => {
      let teamRowCount = 0 // 该团队的总行数
      let teamFirstRowIndex = rows.length // 该团队第一行在rows中的索引

      // 获取周度列结构（取第一个可用的weekly数据作为基准）
      if (weekColumns.length === 0) {
        // 优先从队长数据获取
        if (team.leader_data && team.leader_data.weekly && team.leader_data.weekly.length > 0) {
          weekColumns = team.leader_data.weekly.map((week, index) => ({
            title: this.getWeekTitle(week, index),
            key: `week_${index}`,
            data: week
          }))
        } else if (team.children && team.children.length > 0) {
          // 从第一个小组的第一个组员获取
          const firstGroup = team.children[0]
          if (firstGroup.data && firstGroup.data.length > 0 && firstGroup.data[0].weekly) {
            weekColumns = firstGroup.data[0].weekly.map((week, index) => ({
              title: this.getWeekTitle(week, index),
              key: `week_${index}`,
              data: week
            }))
          }
        }
      }

      // 先生成队长数据行（如果队长有业务数据）
      if (team.leader_data && team.leader_data.weekly) {
        const personId = `team_leader_${team.id}`
        const monthTarget = this.getPersonMonthOrderTarget(team.leader_data)
        const monthActual = this.getPersonMonthOrderActual(team.leader_data)
        const newCustomerAchievement = this.getWeeklyNewCustomerAchievement(team.leader_data)

        // 查找队长所在的小组，获取跟单员信息
        let leaderMerchandiser = ''
        if (team.children && team.children.length > 0) {
          const leaderGroup = team.children.find(group => {
            return group.team_members && Array.isArray(group.team_members) &&
                   group.team_members.some(member => member.id === team.leader_data.id)
          })
          if (leaderGroup) {
            leaderMerchandiser = leaderGroup.merchandiser || ''
          }
        }

        const rowData = {
          id: personId,
          personType: 'team_leader',
          isFirstInTeam: teamRowCount === 0,
          teamRowspan: 0, // 待后续计算

          // 基础信息
          teamName: team.name,
          teamLeader: team.leader,
          teamColor: team.color || '#000000',
          groupLeader: '', // 组长列不显示队长名字
          teamMembers: team.leader, // 组员列显示队长自己的名字
          merchandiser: leaderMerchandiser, // 从包含队长的小组获取跟单员
          month: queryDate ? moment(queryDate).format('YYYY年M月') : '',

          // 团队业绩
          teamOrderTarget: this.getTeamOrderTarget(team),
          teamOrderActual: this.getTeamOrderActual(team),
          teamAchievementRate: this.getWeeklyTeamAchievementRate(team),

          // 小组业绩（队长行不显示具体数值）
          groupOrderTarget: 0,
          groupOrderActual: 0,
          groupAchievementRate: 0,

          // 月度统计
          monthOrderTarget: monthTarget,
          monthOrderActual: monthActual,
          monthAchievementRate: this.getPersonMonthAchievementRate(team.leader_data),
          ranking: monthRankingMap.get(personId) || '-',
          monthGapTarget: this.decimalSubtract(monthTarget, monthActual),
          newCustomerAchievement: newCustomerAchievement,
          newCustomerRatio: this.getWeeklyNewCustomerRatio(team.leader_data),
          oldCustomerRatio: this.getWeeklyOldCustomerRatio(team.leader_data, team.kind),

          // 周度统计
          weeklyData: team.leader_data.weekly.map((week, weekIndex) => ({
            target: this.safeNumber(week.order_target),
            actual: this.safeNumber(week.order_actual),
            achievementRate: this.getWeekAchievementRate(week),
            ranking: weeklyRankingMap.get(`${personId}_${weekIndex}`) || '-'
          }))
        }

        rows.push(rowData)
        teamRowCount++
      }

      // 处理小组数据
      if (team.children && team.children.length > 0) {
        team.children.forEach(group => {
          // 计算该小组的总行数（组长 + 组员）
          let groupRowCount = 0
          if (group.leader && group.leader_data && group.leader_data.weekly) {
            groupRowCount++ // 组长行
          }
          if (group.data && Array.isArray(group.data)) {
            groupRowCount += group.data.filter(member => member.weekly && (!group.leader_data || member.id !== group.leader_data.id)).length
          }

          // 先生成组长数据行
          if (group.leader && group.leader_data && group.leader_data.weekly) {
            const personId = `group_leader_${group.id}`
            const monthTarget = this.getPersonMonthOrderTarget(group.leader_data)
            const monthActual = this.getPersonMonthOrderActual(group.leader_data)
            const newCustomerAchievement = this.getWeeklyNewCustomerAchievement(group.leader_data)

            const rowData = {
              id: personId,
              personType: 'group_leader',
              isFirstInTeam: teamRowCount === 0,
              teamRowspan: 0, // 待后续计算
              isFirstInGroup: true, // 标记为小组第一行
              groupRowspan: groupRowCount, // 小组行跨度

              // 基础信息
              teamName: team.name,
              teamLeader: team.leader,
              teamColor: team.color || '#000000',
              groupLeader: group.leader, // 组长列显示组长名字
              teamMembers: group.leader, // 组员列显示组长自己的名字
              merchandiser: group.merchandiser || '', // 跟单员
              month: queryDate ? moment(queryDate).format('YYYY年M月') : '',

              // 小组信息（用于合并单元格）
              groupId: group.id,
              groupLeaderName: group.leader,
              groupMerchandiser: group.merchandiser || '',

              // 团队业绩
              teamOrderTarget: this.getTeamOrderTarget(team),
              teamOrderActual: this.getTeamOrderActual(team),
              teamAchievementRate: this.getWeeklyTeamAchievementRate(team),

              // 小组业绩
              groupOrderTarget: this.getGroupOrderTarget(group),
              groupOrderActual: this.getGroupOrderActual(group),
              groupAchievementRate: this.getGroupAchievementRate(group),

              // 月度统计
              monthOrderTarget: monthTarget,
              monthOrderActual: monthActual,
              monthAchievementRate: this.getPersonMonthAchievementRate(group.leader_data),
              ranking: monthRankingMap.get(personId) || '-',
              monthGapTarget: this.decimalSubtract(monthTarget, monthActual),
              newCustomerAchievement: newCustomerAchievement,
              newCustomerRatio: this.getWeeklyNewCustomerRatio(group.leader_data),
              oldCustomerRatio: this.getWeeklyOldCustomerRatio(group.leader_data, group.kind),

              // 周度统计
              weeklyData: group.leader_data.weekly.map((week, weekIndex) => ({
                target: this.safeNumber(week.order_target),
                actual: this.safeNumber(week.order_actual),
                achievementRate: this.getWeekAchievementRate(week),
                ranking: weeklyRankingMap.get(`${personId}_${weekIndex}`) || '-'
              }))
            }

            rows.push(rowData)
            teamRowCount++
          }

          // 生成组员数据行（排除组长）
          if (group.data && Array.isArray(group.data)) {
            group.data.forEach((member, memberIndex) => {
              // 跳过组长，因为组长已经单独处理了
              if (member.weekly && (!group.leader_data || member.id !== group.leader_data.id)) {
                const personId = `member_${group.id}_${memberIndex}`
                const monthTarget = this.getPersonMonthOrderTarget(member)
                const monthActual = this.getPersonMonthOrderActual(member)
                const newCustomerAchievement = this.getWeeklyNewCustomerAchievement(member)

                const rowData = {
                  id: personId,
                  personType: 'member',
                  isFirstInTeam: teamRowCount === 0,
                  teamRowspan: 0, // 待后续计算
                  isFirstInGroup: false, // 不是小组第一行
                  groupRowspan: 0, // 组员行不需要跨度

                  // 基础信息
                  teamName: team.name,
                  teamLeader: team.leader,
                  teamColor: team.color || '#000000',
                  groupLeader: group.leader || '', // 组长列显示小组的组长名字（但会被合并隐藏）
                  teamMembers: member.name || '', // 组员列显示组员姓名
                  merchandiser: group.merchandiser || '', // 跟单员（但会被合并隐藏）
                  month: queryDate ? moment(queryDate).format('YYYY年M月') : '',

                  // 小组信息（用于合并单元格）
                  groupId: group.id,
                  groupLeaderName: group.leader,
                  groupMerchandiser: group.merchandiser || '',

                  // 团队业绩
                  teamOrderTarget: this.getTeamOrderTarget(team),
                  teamOrderActual: this.getTeamOrderActual(team),
                  teamAchievementRate: this.getWeeklyTeamAchievementRate(team),

                  // 小组业绩（组员行不显示独立的小组业绩，会被合并隐藏）
                  groupOrderTarget: this.getGroupOrderTarget(group),
                  groupOrderActual: this.getGroupOrderActual(group),
                  groupAchievementRate: this.getGroupAchievementRate(group),

                  // 月度统计
                  monthOrderTarget: monthTarget,
                  monthOrderActual: monthActual,
                  monthAchievementRate: this.getPersonMonthAchievementRate(member),
                  ranking: monthRankingMap.get(personId) || '-',
                  monthGapTarget: this.decimalSubtract(monthTarget, monthActual),
                  newCustomerAchievement: newCustomerAchievement,
                  newCustomerRatio: this.getWeeklyNewCustomerRatio(member),
                  oldCustomerRatio: this.getWeeklyOldCustomerRatio(member, team.kind),

                  // 周度统计
                  weeklyData: member.weekly.map((week, weekIndex) => ({
                    target: this.safeNumber(week.order_target),
                    actual: this.safeNumber(week.order_actual),
                    achievementRate: this.getWeekAchievementRate(week),
                    ranking: weeklyRankingMap.get(`${personId}_${weekIndex}`) || '-'
                  }))
                }

                rows.push(rowData)
                teamRowCount++
              }
            })
          }
        })
      }

      // 回填团队行跨度
      if (teamRowCount > 0) {
        rows[teamFirstRowIndex].teamRowspan = teamRowCount
      }
    })

    return { rows, weekColumns }
  }

  /**
   * 处理半年目标表格数据
   * @param {array} teamsData - 原始团队数据
   * @param {string} queryDate - 查询日期
   * @returns {object} 处理后的表格数据
   */
  static processHalfYearlyTableData(teamsData) {
    if (!Array.isArray(teamsData)) {
      return { rows: [], weekColumns: [] }
    }

    // 计算排名（需要更新为新的数据结构）
    const rankingMap = this.calculateHalfYearlyRankingNew(teamsData)

    const rows = []

    teamsData.forEach(team => {
      let teamRowCount = 0 // 该团队的总行数
      let teamFirstRowIndex = rows.length // 该团队第一行在rows中的索引

      // 先生成队长数据行（如果队长有业务数据）
      if (team.leader_data && team.leader_data.half_yearly && team.leader_data.half_yearly[0]) {
        const halfYearlyData = team.leader_data.half_yearly[0]
        const personId = `team_leader_${team.id}`

        // 计算新客户相关数据
        const newCustomerTotal = this.calculateNewCustomerTotal(halfYearlyData.new_customer_order_actual)
        const goldenOldCustomerTotal = this.calculateGoldenOldCustomerTotal(halfYearlyData.golden_old_customer_order_actual)
        const orderActual = this.safeNumber(halfYearlyData.order_actual)
        const orderTarget = this.safeNumber(halfYearlyData.order_target)

        // 查找队长所在的小组，获取跟单员信息
        let leaderMerchandiser = ''
        if (team.children && team.children.length > 0) {
          const leaderGroup = team.children.find(group => {
            return group.team_members && Array.isArray(group.team_members) &&
                   group.team_members.some(member => member.id === team.leader_data.id)
          })
          if (leaderGroup) {
            leaderMerchandiser = leaderGroup.merchandiser || ''
          }
        }

        // 计算各项指标
        const halfYearAchievementRate = this.decimalDivide(orderActual, orderTarget)
        const newCustomerRatio = this.decimalDivide(newCustomerTotal, orderActual)
        let oldCustomerRatio = 0
        if (team.kind === 'golden') {
          oldCustomerRatio = this.decimalDivide(goldenOldCustomerTotal, orderActual)
        } else {
          const oldCustomerValue = this.decimalSubtract(orderActual, newCustomerTotal)
          oldCustomerRatio = this.decimalDivide(oldCustomerValue, orderActual)
        }
        const gapTarget = Math.max(0, this.decimalSubtract(orderTarget, orderActual))
        const ranking = rankingMap.get(personId) || '-'

        const rowData = {
          id: personId,
          personType: 'team_leader',
          isFirstInTeam: teamRowCount === 0,
          teamRowspan: 0, // 待后续计算
          groupRowspan: 1, // 队长行不需要合并，设置为1

          // 基础信息
          teamName: team.name,
          teamLeader: team.leader,
          teamColor: team.color || '#000000',
          groupLeader: '', // 组长列不显示队长名字
          teamMembers: team.leader, // 组员列显示队长自己的名字
          merchandiser: leaderMerchandiser, // 从包含队长的小组获取跟单员
          halfYearPeriod: this.getHalfYearLabel(halfYearlyData.start_date),

          // 团队业绩
          teamHalfYearOrderTarget: this.getTeamHalfYearOrderTarget(team),
          teamHalfYearOrderActual: this.getTeamHalfYearOrderActual(team),
          teamHalfYearAchievementRate: this.getTeamHalfYearAchievementRate(team),

          // 小组业绩（队长行显示"-"）
          groupHalfYearOrderTarget: '-',
          groupHalfYearOrderActual: '-',
          groupHalfYearAchievementRate: '-',

          // 半年度统计
          teamAchievementRate: this.calculateTeamAchievementRateNew(team),
          halfYearOrderTarget: orderTarget,
          halfYearOrderActual: orderActual,
          halfYearAchievementRate,
          ranking,
          gapTarget,
          newCustomerAchievement: newCustomerTotal,
          newCustomerRatio,
          oldCustomerRatio
        }

        rows.push(rowData)
        teamRowCount++
      }

      // 处理小组数据
      if (team.children && team.children.length > 0) {
        team.children.forEach(group => {
          // 计算该小组的总行数（组长 + 组员）
          let groupRowCount = 0
          if (group.leader_data && group.leader_data.half_yearly && group.leader_data.half_yearly[0]) {
            groupRowCount++ // 组长行
          }
          if (group.data && Array.isArray(group.data)) {
            groupRowCount += group.data.filter(member =>
              member.half_yearly && member.half_yearly[0] &&
              (!group.leader_data || member.id !== group.leader_data.id)
            ).length
          }

          // 先生成组长数据行
          if (group.leader_data && group.leader_data.half_yearly && group.leader_data.half_yearly[0]) {
            const halfYearlyData = group.leader_data.half_yearly[0]
            const personId = `group_leader_${group.id}`

            // 计算新客户相关数据
            const newCustomerTotal = this.calculateNewCustomerTotal(halfYearlyData.new_customer_order_actual)
            const goldenOldCustomerTotal = this.calculateGoldenOldCustomerTotal(halfYearlyData.golden_old_customer_order_actual)
            const orderActual = this.safeNumber(halfYearlyData.order_actual)
            const orderTarget = this.safeNumber(halfYearlyData.order_target)

            // 计算各项指标
            const halfYearAchievementRate = this.decimalDivide(orderActual, orderTarget)
            const newCustomerRatio = this.decimalDivide(newCustomerTotal, orderActual)
            let oldCustomerRatio = 0
            if (team.kind === 'golden') {
              oldCustomerRatio = this.decimalDivide(goldenOldCustomerTotal, orderActual)
            } else {
              const oldCustomerValue = this.decimalSubtract(orderActual, newCustomerTotal)
              oldCustomerRatio = this.decimalDivide(oldCustomerValue, orderActual)
            }
            const gapTarget = Math.max(0, this.decimalSubtract(orderTarget, orderActual))
            const ranking = rankingMap.get(personId) || '-'

            const rowData = {
              id: personId,
              personType: 'group_leader',
              isFirstInTeam: teamRowCount === 0,
              teamRowspan: 0, // 待后续计算
              isFirstInGroup: true, // 标记为小组第一行
              groupRowspan: groupRowCount, // 小组行跨度

              // 基础信息
              teamName: team.name,
              teamLeader: team.leader,
              teamColor: team.color || '#000000',
              groupLeader: group.leader, // 组长列显示组长名字
              teamMembers: group.leader, // 组员列显示组长自己的名字
              merchandiser: group.merchandiser || '', // 跟单员
              halfYearPeriod: this.getHalfYearLabel(halfYearlyData.start_date),

              // 小组信息（用于合并单元格）
              groupId: group.id,
              groupLeaderName: group.leader,
              groupMerchandiser: group.merchandiser || '',

              // 团队业绩
              teamHalfYearOrderTarget: this.getTeamHalfYearOrderTarget(team),
              teamHalfYearOrderActual: this.getTeamHalfYearOrderActual(team),
              teamHalfYearAchievementRate: this.getTeamHalfYearAchievementRate(team),

              // 小组业绩
              groupHalfYearOrderTarget: this.getGroupHalfYearOrderTarget(group),
              groupHalfYearOrderActual: this.getGroupHalfYearOrderActual(group),
              groupHalfYearAchievementRate: this.getGroupHalfYearAchievementRate(group),

              // 半年度统计
              teamAchievementRate: this.calculateTeamAchievementRateNew(team),
              halfYearOrderTarget: orderTarget,
              halfYearOrderActual: orderActual,
              halfYearAchievementRate,
              ranking,
              gapTarget,
              newCustomerAchievement: newCustomerTotal,
              newCustomerRatio,
              oldCustomerRatio
            }

            rows.push(rowData)
            teamRowCount++
          }

          // 生成组员数据行（排除组长）
          if (group.data && Array.isArray(group.data)) {
            group.data.forEach((member, memberIndex) => {
              // 跳过组长，因为组长已经单独处理了
              if (member.half_yearly && member.half_yearly[0] && (!group.leader_data || member.id !== group.leader_data.id)) {
                const halfYearlyData = member.half_yearly[0]
                const personId = `member_${group.id}_${memberIndex}`

                // 计算新客户相关数据
                const newCustomerTotal = this.calculateNewCustomerTotal(halfYearlyData.new_customer_order_actual)
                const goldenOldCustomerTotal = this.calculateGoldenOldCustomerTotal(halfYearlyData.golden_old_customer_order_actual)
                const orderActual = this.safeNumber(halfYearlyData.order_actual)
                const orderTarget = this.safeNumber(halfYearlyData.order_target)

                // 计算各项指标
                const halfYearAchievementRate = this.decimalDivide(orderActual, orderTarget)
                const newCustomerRatio = this.decimalDivide(newCustomerTotal, orderActual)
                let oldCustomerRatio = 0
                if (team.kind === 'golden') {
                  oldCustomerRatio = this.decimalDivide(goldenOldCustomerTotal, orderActual)
                } else {
                  const oldCustomerValue = this.decimalSubtract(orderActual, newCustomerTotal)
                  oldCustomerRatio = this.decimalDivide(oldCustomerValue, orderActual)
                }
                const gapTarget = Math.max(0, this.decimalSubtract(orderTarget, orderActual))
                const ranking = rankingMap.get(`member_${member.id}`) || '-'

                const rowData = {
                  id: personId,
                  personType: 'member',
                  isFirstInTeam: teamRowCount === 0,
                  teamRowspan: 0, // 待后续计算
                  isFirstInGroup: false, // 不是小组第一行
                  groupRowspan: 0, // 组员行不需要跨度

                  // 基础信息
                  teamName: team.name,
                  teamLeader: team.leader,
                  teamColor: team.color || '#000000',
                  groupLeader: group.leader || '', // 组长列显示小组的组长名字（但会被合并隐藏）
                  teamMembers: member.name || '', // 组员列显示组员姓名
                  merchandiser: group.merchandiser || '', // 跟单员（但会被合并隐藏）
                  halfYearPeriod: this.getHalfYearLabel(halfYearlyData.start_date),

                  // 小组信息（用于合并单元格）
                  groupId: group.id,
                  groupLeaderName: group.leader,
                  groupMerchandiser: group.merchandiser || '',

                  // 团队业绩
                  teamHalfYearOrderTarget: this.getTeamHalfYearOrderTarget(team),
                  teamHalfYearOrderActual: this.getTeamHalfYearOrderActual(team),
                  teamHalfYearAchievementRate: this.getTeamHalfYearAchievementRate(team),

                  // 小组业绩（组员行显示同组长行相同数据）
                  groupHalfYearOrderTarget: this.getGroupHalfYearOrderTarget(group),
                  groupHalfYearOrderActual: this.getGroupHalfYearOrderActual(group),
                  groupHalfYearAchievementRate: this.getGroupHalfYearAchievementRate(group),

                  // 半年度统计
                  teamAchievementRate: this.calculateTeamAchievementRateNew(team),
                  halfYearOrderTarget: orderTarget,
                  halfYearOrderActual: orderActual,
                  halfYearAchievementRate,
                  ranking,
                  gapTarget,
                  newCustomerAchievement: newCustomerTotal,
                  newCustomerRatio,
                  oldCustomerRatio
                }

                rows.push(rowData)
                teamRowCount++
              }
            })
          }
        })
      }

      // 回填团队行跨度
      if (teamRowCount > 0) {
        rows[teamFirstRowIndex].teamRowspan = teamRowCount
      }
    })

    return {
      rows,
      weekColumns: [] // 半年目标没有周列
    }
  }

  /**
   * 计算新数据结构的半年度排名
   * @param {array} teamsData - 所有团队数据
   * @returns {Map} ID到排名的映射
   */
  static calculateHalfYearlyRankingNew(teamsData) {
    const rankingMap = new Map()
    const allPersons = []
    const processedIds = new Set() // 记录已处理的用户ID，避免重复计算

    // 收集所有人员数据
    teamsData.forEach(team => {
      // 收集队长数据
      if (team.leader_data && team.leader_data.half_yearly && team.leader_data.half_yearly[0] && team.leader_data.id) {
        if (!processedIds.has(team.leader_data.id)) {
          const orderActual = this.safeNumber(team.leader_data.half_yearly[0].order_actual)
          allPersons.push({
            personId: `team_leader_${team.id}`,
            orderActual: orderActual,
            userId: team.leader_data.id
          })
          processedIds.add(team.leader_data.id)
        }
      }

      // 收集小组数据（跳过已处理的ID）
      if (team.children) {
        team.children.forEach(group => {
          // 组长数据（检查是否已处理过）
          if (group.leader_data && group.leader_data.half_yearly && group.leader_data.half_yearly[0] && group.leader_data.id) {
            if (!processedIds.has(group.leader_data.id)) {
              const orderActual = this.safeNumber(group.leader_data.half_yearly[0].order_actual)
              allPersons.push({
                personId: `group_leader_${group.id}`,
                orderActual: orderActual,
                userId: group.leader_data.id
              })
              processedIds.add(group.leader_data.id)
            }
          }

          // 组员数据（排除组长，并检查是否已处理过）
          if (group.data && Array.isArray(group.data)) {
            group.data.forEach(member => {
              if (member.half_yearly && member.half_yearly[0] && member.id &&
                  (!group.leader_data || member.id !== group.leader_data.id) &&
                  !processedIds.has(member.id)) {
                const orderActual = this.safeNumber(member.half_yearly[0].order_actual)
                allPersons.push({
                  personId: `member_${member.id}`,
                  orderActual: orderActual,
                  userId: member.id
                })
                processedIds.add(member.id)
              }
            })
          }
        })
      }
    })

    // 分离有效数据和零值数据
    const validPersons = allPersons.filter(person => person.orderActual > 0)
    const zeroPersons = allPersons.filter(person => person.orderActual === 0)

    // 按达成金额降序排序
    validPersons.sort((a, b) => b.orderActual - a.orderActual)

    // 计算排名（支持并列排名）
    let currentRank = 1
    let previousValue = null

    validPersons.forEach((person, index) => {
      if (previousValue !== null && person.orderActual !== previousValue) {
        // 数值不同，更新排名（跳跃式排名）
        currentRank = index + 1
      }

      rankingMap.set(person.personId, currentRank)
      previousValue = person.orderActual
    })

    // 零值数据统一显示"-"
    zeroPersons.forEach(person => {
      rankingMap.set(person.personId, '-')
    })

    return rankingMap
  }

  /**
   * 计算新数据结构的团队达成率
   * @param {object} team - 团队数据
   * @returns {number} 团队达成率
   */
  static calculateTeamAchievementRateNew(team) {
    const targetValues = []
    const actualValues = []
    const processedIds = new Set() // 记录已处理的用户ID，避免重复计算

    // 队长数据
    if (team.leader_data && team.leader_data.half_yearly && team.leader_data.half_yearly[0] && team.leader_data.id) {
      targetValues.push(this.safeNumber(team.leader_data.half_yearly[0].order_target))
      actualValues.push(this.safeNumber(team.leader_data.half_yearly[0].order_actual))
      processedIds.add(team.leader_data.id)
    }

    // 小组数据（跳过已处理的ID）
    if (team.children && team.children.length > 0) {
      team.children.forEach(group => {
        // 组长数据（检查是否已处理过）
        if (group.leader_data && group.leader_data.half_yearly && group.leader_data.half_yearly[0] && group.leader_data.id) {
          if (!processedIds.has(group.leader_data.id)) {
            targetValues.push(this.safeNumber(group.leader_data.half_yearly[0].order_target))
            actualValues.push(this.safeNumber(group.leader_data.half_yearly[0].order_actual))
            processedIds.add(group.leader_data.id)
          }
        }

        // 组员数据（排除组长，并检查是否已处理过）
        if (group.data && Array.isArray(group.data)) {
          group.data.forEach(member => {
            if (member.half_yearly && member.half_yearly[0] && member.id &&
                (!group.leader_data || member.id !== group.leader_data.id) &&
                !processedIds.has(member.id)) {
              targetValues.push(this.safeNumber(member.half_yearly[0].order_target))
              actualValues.push(this.safeNumber(member.half_yearly[0].order_actual))
              processedIds.add(member.id)
            }
          })
        }
      })
    }

    const totalTarget = this.decimalSum(targetValues)
    const totalActual = this.decimalSum(actualValues)
    return this.decimalDivide(totalActual, totalTarget)
  }

  /**
   * 计算新数据结构的团队行跨度
   * @param {object} team - 团队数据
   * @returns {number} 团队行跨度
   */
  static calculateTeamRowspanNew(team) {
    if (!team.children || team.children.length === 0) return 1

    let totalRows = 0

    team.children.forEach(group => {
      // 组长行
      if (group.leader_data && group.leader_data.half_yearly && group.leader_data.half_yearly[0]) {
        totalRows++
      }

      // 组员行
      if (group.data && Array.isArray(group.data)) {
        group.data.forEach(member => {
          if (member.half_yearly && member.half_yearly[0]) {
            totalRows++
          }
        })
      }
    })

    return totalRows || 1
  }

  // ====================
  // 半年度团队和小组业绩计算方法
  // ====================

  /**
   * 获取团队半年度订单目标
   * @param {object} team - 团队数据
   * @returns {number} 团队半年度订单目标
   */
  static getTeamHalfYearOrderTarget(team) {
    if (!team) return 0
    const values = []
    const processedIds = new Set() // 记录已处理的用户ID，避免重复计算

    // 统计队长目标
    if (team.leader_data && team.leader_data.half_yearly && team.leader_data.half_yearly[0] && team.leader_data.id) {
      values.push(this.safeNumber(team.leader_data.half_yearly[0].order_target))
      processedIds.add(team.leader_data.id)
    }

    // 统计小组目标，跳过已处理的ID
    if (team.children && team.children.length > 0) {
      team.children.forEach(group => {
        values.push(this.getGroupHalfYearOrderTarget(group, processedIds))
      })
    }

    return this.decimalSum(values)
  }

  /**
   * 获取团队半年度订单实际
   * @param {object} team - 团队数据
   * @returns {number} 团队半年度订单实际
   */
  static getTeamHalfYearOrderActual(team) {
    if (!team) return 0
    const values = []
    const processedIds = new Set() // 记录已处理的用户ID，避免重复计算

    // 统计队长实际
    if (team.leader_data && team.leader_data.half_yearly && team.leader_data.half_yearly[0] && team.leader_data.id) {
      values.push(this.safeNumber(team.leader_data.half_yearly[0].order_actual))
      processedIds.add(team.leader_data.id)
    }

    // 统计小组实际，跳过已处理的ID
    if (team.children && team.children.length > 0) {
      team.children.forEach(group => {
        values.push(this.getGroupHalfYearOrderActual(group, processedIds))
      })
    }

    return this.decimalSum(values)
  }

  /**
   * 获取团队半年度达成率
   * @param {object} team - 团队数据
   * @returns {number} 团队半年度达成率
   */
  static getTeamHalfYearAchievementRate(team) {
    const target = this.getTeamHalfYearOrderTarget(team)
    const actual = this.getTeamHalfYearOrderActual(team)
    return this.decimalDivide(actual, target)
  }

  /**
   * 获取小组半年度订单目标
   * @param {object} group - 小组数据
   * @param {Set} processedIds - 已处理的用户ID集合（可选），用于避免重复计算
   * @returns {number} 小组半年度订单目标
   */
  static getGroupHalfYearOrderTarget(group, processedIds = null) {
    if (!group) return 0
    const values = []

    // 统计组长目标（检查是否已处理过）
    if (group.leader_data && group.leader_data.half_yearly && group.leader_data.half_yearly[0] && group.leader_data.id) {
      if (!processedIds || !processedIds.has(group.leader_data.id)) {
        values.push(this.safeNumber(group.leader_data.half_yearly[0].order_target))
        if (processedIds) {
          processedIds.add(group.leader_data.id)
        }
      }
    }

    // 统计组员目标（排除组长，并检查是否已处理过）
    if (group.data && Array.isArray(group.data)) {
      group.data.forEach(member => {
        if (member.half_yearly && member.half_yearly[0] && member.id &&
            (!group.leader_data || member.id !== group.leader_data.id) &&
            (!processedIds || !processedIds.has(member.id))) {
          values.push(this.safeNumber(member.half_yearly[0].order_target))
          if (processedIds) {
            processedIds.add(member.id)
          }
        }
      })
    }

    return this.decimalSum(values)
  }

  /**
   * 获取小组半年度订单实际
   * @param {object} group - 小组数据
   * @param {Set} processedIds - 已处理的用户ID集合（可选），用于避免重复计算
   * @returns {number} 小组半年度订单实际
   */
  static getGroupHalfYearOrderActual(group, processedIds = null) {
    if (!group) return 0
    const values = []

    // 统计组长实际（检查是否已处理过）
    if (group.leader_data && group.leader_data.half_yearly && group.leader_data.half_yearly[0] && group.leader_data.id) {
      if (!processedIds || !processedIds.has(group.leader_data.id)) {
        values.push(this.safeNumber(group.leader_data.half_yearly[0].order_actual))
        if (processedIds) {
          processedIds.add(group.leader_data.id)
        }
      }
    }

    // 统计组员实际（排除组长，并检查是否已处理过）
    if (group.data && Array.isArray(group.data)) {
      group.data.forEach(member => {
        if (member.half_yearly && member.half_yearly[0] && member.id &&
            (!group.leader_data || member.id !== group.leader_data.id) &&
            (!processedIds || !processedIds.has(member.id))) {
          values.push(this.safeNumber(member.half_yearly[0].order_actual))
          if (processedIds) {
            processedIds.add(member.id)
          }
        }
      })
    }

    return this.decimalSum(values)
  }

  /**
   * 获取小组半年度达成率
   * @param {object} group - 小组数据
   * @returns {number} 小组半年度达成率
   */
  static getGroupHalfYearAchievementRate(group) {
    const target = this.getGroupHalfYearOrderTarget(group)
    const actual = this.getGroupHalfYearOrderActual(group)
    return this.decimalDivide(actual, target)
  }
}
